import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Lock, Eye } from "lucide-react";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { CourseSidebar as CourseSidebarType } from "../types/lectureTypes";

interface CourseSidebarWithoutPermissionProps {
  sidebar: CourseSidebarType;
  currentLectureId: string;
}

export function CourseSidebarWithoutPermission({
  sidebar,
  currentLectureId,
}: CourseSidebarWithoutPermissionProps) {
  const { t } = useTranslation();
  const { courseId } = useParams();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(
      sidebar.sections
        .filter((section) =>
          section.lectures.some((lecture) => lecture.id === currentLectureId),
        )
        .map((section) => section.id),
    ),
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const formatDuration = (duration: string) => {
    // TimeSpan formatı: "00:15:30" -> "15dk 30sn"
    const parts = duration.split(":");
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);
    const seconds = parseInt(parts[2]);

    if (hours > 0) {
      return t("lecture.duration.hoursMinutes", { hours, minutes });
    }
    if (minutes > 0) {
      return t("lecture.duration.minutesSeconds", { minutes, seconds });
    }
    return t("lecture.duration.seconds", { seconds });
  };

  return (
    <div className="h-full flex flex-col">
      <CardHeader className="border-b border-gray-200 dark:border-gray-700">
        <CardTitle className="text-gray-900 dark:text-white">
          {sidebar.name}
        </CardTitle>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {t("lecture.sidebar.previewMode")}
        </p>
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto p-0">
        {sidebar.sections.map((section) => {
          const isExpanded = expandedSections.has(section.id);

          return (
            <div
              key={section.id}
              className="border-b border-gray-200 dark:border-gray-700 last:border-b-0"
            >
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                    <span className="font-medium text-gray-900 dark:text-white">
                      {section.name}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t("lecture.sidebar.lectureCount", { count: section.lectures.length })}
                  </span>
                </div>
              </button>

              {/* Section Lectures */}
              {isExpanded && (
                <div className="pb-2">
                  {section.lectures.map((lecture, index) => {
                    const isCurrentLecture = lecture.id === currentLectureId;
                    const isAccessible = lecture.isPublic;

                    return (
                      <div key={lecture.id}>
                        {index > 0 && (
                          <hr className="border-gray-200 dark:border-gray-700" />
                        )}
                        <div
                          className={cn(
                            "px-6 py-3 border-l-2 transition-colors",
                            isCurrentLecture
                              ? "border-primary-600 bg-primary-50 dark:bg-primary-500"
                              : "border-transparent bg-gray-100 dark:bg-gray-900 hover:bg-gray-200 dark:hover:bg-gray-800",
                          )}
                        >
                          {isAccessible ? (
                            <Link
                              to={`/course/${courseId}/${lecture.id}`}
                              className="block"
                            >
                              <div className="flex items-center gap-3">
                                <Eye
                                  className={cn(
                                    "w-4 h-4 flex-shrink-0",
                                    isCurrentLecture
                                      ? "text-primary-600 dark:text-primary-500"
                                      : "text-primary-600 dark:text-primary-500",
                                  )}
                                />
                                <div className="flex-grow">
                                  <p
                                    className={cn(
                                      "text-sm font-medium",
                                      isCurrentLecture
                                        ? "text-primary-500 dark:text-primary-100"
                                        : "text-gray-900 dark:text-white",
                                    )}
                                  >
                                    {lecture.name}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatDuration(lecture.duration)}
                                  </p>
                                </div>
                              </div>
                            </Link>
                          ) : (
                            <div className="flex items-center gap-3 opacity-60 cursor-not-allowed">
                              <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <div className="flex-grow">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                  {lecture.name}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  {formatDuration(lecture.duration)}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </div>
  );
}
